import React from 'react';
import { useWindowWidth } from './useWindowWidth';

const BRAND = {
  navy: '#1a2a5e',
  navyDeep: '#0b1228',
  red: '#c41230',
  white: '#ffffff',
  offWhite: '#e8eaf0',
  offWhiteDim: 'rgba(232,234,240,0.65)',
  offWhiteFaint: 'rgba(232,234,240,0.35)',
  panelBg: 'rgba(26,42,94,0.35)',
  panelBorder: 'rgba(255,255,255,0.09)',
  borderStrong: 'rgba(255,255,255,0.18)',
};

export default function HomePage({ onNavigate }) {
  const width = useWindowWidth();
  const isMobile = width < 768;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      minHeight: 'calc(100vh - 188px)',
      paddingTop: 32,
      boxSizing: 'border-box',
    }}>

      {/* Left logo — desktop only */}
      {!isMobile ? (
        <div style={{
          width: '15%', flexShrink: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          paddingTop: 16, paddingLeft: 8, paddingRight: 8,
          borderRight: `1px solid rgba(255,255,255,0.09)`,
          position: 'sticky', top: 0, alignSelf: 'flex-start',
          height: 'calc(100vh - 188px)',
          boxSizing: 'border-box',
        }}>
          <img src="/LogoTall.png" alt="CyberMod.io" style={{ width: '100%', opacity: 1 }} />
        </div>
      ) : null}

      {/* Centre content */}
      <div style={{
        width: isMobile ? '100%' : '70%',
        flexShrink: 0,
        padding: isMobile ? '24px 16px 60px' : '32px 48px 80px',
        boxSizing: 'border-box',
      }}>

      {/* Tag line */}
      <div style={{
        display: 'inline-block',
        fontFamily: "'Cinzel', serif",
        fontSize: 9,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: BRAND.red,
        border: `1px solid ${BRAND.red}`,
        padding: '3px 10px',
        borderRadius: 2,
        marginBottom: 28,
        opacity: 0.9,
      }}>
        About
      </div>

      {/* Intro */}
      <div style={{
        fontFamily: "'Crimson Pro', serif",
        fontSize: isMobile ? 16 : 18,
        lineHeight: 1.85,
        color: BRAND.offWhite,
      }}>
        <p style={{ marginBottom: '1.3em' }}>
          Hi, I'm a cybersecurity professional with 20+ years in IT, which mostly means I've spent
          a career watching people click things they shouldn't and then dealing with the fallout.
        </p>

        <p style={{ marginBottom: '1.3em' }}>
          My main focus now is <strong style={{ color: BRAND.white }}>identity</strong>. Not the
          buzzword version, the messy reality of credentials, access, privilege, and how attackers
          exploit them. If someone can log in as someone else, most of your "security" is just
          decoration. I've worked from hands-on engineering to architecture, led ransomware
          responses, and regularly translate identity risk into business terms that don't get ignored.
        </p>
        {/* Divider */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        margin: '40px 0',
      }}>
        <div style={{ flex: 1, height: 1, background: BRAND.panelBorder }} />
        <div style={{
          fontFamily: "'Cinzel', serif", fontSize: 16,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          color: BRAND.offWhiteDim,
        }}>
          The blog
        </div>
        <div style={{ flex: 1, height: 1, background: BRAND.panelBorder }} />
      </div>

        <p style={{ marginBottom: '1.3em' }}>
          I've spent years breaking and fixing identity systems, watching vendor pitches age about as well as milk in the sun, and sitting through post-mortems that had no right lasting that long. Identity is where hype goes to die slowly, and I've seen enough trends roll in like they own the place and slip out the back to have opinions worth arguing with. This is me thinking out loud about what it all actually means: part hard-won lessons, part post-mortem therapy, part rant nobody asked for but you're getting anyway.
        </p>
      </div>

      {/* Divider */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 16,
        margin: '40px 0',
      }}>
        <div style={{ flex: 1, height: 1, background: BRAND.panelBorder }} />
        <div style={{
          fontFamily: "'Cinzel', serif", fontSize: 16,
          letterSpacing: '0.2em', textTransform: 'uppercase',
          color: BRAND.offWhiteDim,
        }}>
          And then there's the miniatures
        </div>
        <div style={{ flex: 1, height: 1, background: BRAND.panelBorder }} />
      </div>

      {/* Miniatures section */}
      <div style={{
        fontFamily: "'Crimson Pro', serif",
        fontSize: 18,
        lineHeight: 1.85,
        color: BRAND.offWhite,
      }}>
        <p style={{ marginBottom: '1.3em' }}>
          Because apparently two decades in the trenches of IT wasn't enough of a hobby, I recently
          picked up miniature painting. I am, without any ambiguity, a complete beginner. My first
          models looked like they'd been painted by someone wearing oven mitts in a moving vehicle.
          They've improved since then...marginally.
        </p>

        <p style={{ marginBottom: '1.3em' }}>
          The <strong
            onClick={() => onNavigate('palette')}
            style={{
              color: BRAND.white, cursor: 'pointer',
              borderBottom: `1px solid ${BRAND.red}`,
              paddingBottom: 1,
            }}
          >Palette</strong> tab above is a colour theory tool I built to help me figure out which
          paints actually go together. If you're also new to the hobby and find yourself staring at
          a rack of 200 Citadel pots wondering why your Space Marine looks like a bruise, that tool
          is for you too.
        </p>

        <blockquote style={{
          borderLeft: `3px solid ${BRAND.red}`,
          paddingLeft: 20,
          margin: '2em 0',
          fontStyle: 'italic',
          color: BRAND.offWhiteDim,
          fontSize: 17,
        }}>
          The gap between knowing what looks good and being able to execute it with a size 2 brush
          is, it turns out, enormous.
        </blockquote>

        <p>
          Expect the odd post about painting progress alongside the security content. Two very
          different disciplines, both requiring patience, attention to detail, and the ability to
          recover gracefully from catastrophic mistakes.
        </p>
      </div>

      {/* CTA */}
      <div style={{ marginTop: 52, display: 'flex', gap: 12 }}>
        <button
          onClick={() => onNavigate('cyberblog')}
          style={{
            padding: '10px 24px',
            background: BRAND.red,
            border: `1px solid ${BRAND.red}`,
            borderRadius: 3,
            color: BRAND.white,
            fontFamily: "'Cinzel', serif",
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          Read the Blog
        </button>
        <button
          onClick={() => onNavigate('miniblog')}
          style={{
            padding: '10px 24px',
            background: BRAND.red,
            border: `1px solid ${BRAND.red}`,
            borderRadius: 3,
            color: BRAND.white,
            fontFamily: "'Cinzel', serif",
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          Read the Mini-Blog
        </button>
        <button
          onClick={() => onNavigate('palette')}
          style={{
            padding: '10px 24px',
            background: 'transparent',
            border: `1px solid ${BRAND.borderStrong}`,
            borderRadius: 3,
            color: BRAND.offWhiteDim,
            fontFamily: "'Cinzel', serif",
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND.red; e.currentTarget.style.color = BRAND.white; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = BRAND.borderStrong; e.currentTarget.style.color = BRAND.offWhiteDim; }}
        >
          Open Palette
        </button>
      </div>

      </div>{/* end centre content */}

      {/* Right logo — desktop only */}
      {!isMobile ? (
        <div style={{
          width: '15%', flexShrink: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          paddingTop: 16, paddingLeft: 8, paddingRight: 8,
          borderLeft: `1px solid rgba(255,255,255,0.09)`,
          position: 'sticky', top: 0, alignSelf: 'flex-start',
          height: 'calc(100vh - 188px)',
          boxSizing: 'border-box',
        }}>
          <img src="/LogoTall.png" alt="CyberMod.io" style={{ width: '100%', opacity: 1 }} />
        </div>
      ) : null}

    </div>
  );
}
